import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend, { type BackendError } from "@/utils/fetchToBackend";
import { companyKeys, viewQuotaKeys } from "@/lib/query-keys";
import { toastUtil } from "@/utils/toastUtil";
import { capture } from "@/lib/posthog";
import { useUpsell } from "@/providers/UpsellProvider";

/**
 * Desbloquea una empresa (nombre, RUC y contacto) consumiendo una vista. No
 * desbloquea sus licitaciones. Sin vistas, abre el upsell como las licitaciones.
 */
export function useUnlockCompany() {
  const qc = useQueryClient();
  const openUpsell = useUpsell();
  return useMutation<void, BackendError, { companyId: number }>({
    mutationFn: ({ companyId }) =>
      fetchToBackend<void>(`/companies/${companyId}/unlock`, { method: "POST" }),
    onSuccess: (_data, { companyId }) => {
      capture("company_unlocked", { company_id: companyId });
      qc.invalidateQueries({ queryKey: companyKeys.all });
      qc.invalidateQueries({ queryKey: viewQuotaKeys.all });
    },
    onError: (err) => {
      if (err?.errorCode === "NO_VIEWS_LEFT") {
        openUpsell("no-views");
        return;
      }
      toastUtil("No se pudo desbloquear la empresa.", "error");
    },
  });
}

/** Sigue o deja de seguir una empresa. Seguir exige tenerla desbloqueada y un plan pagado. */
export function useToggleFollowCompany() {
  const qc = useQueryClient();
  return useMutation<void, BackendError, { companyId: number; follow: boolean }>({
    mutationFn: ({ companyId, follow }) =>
      fetchToBackend<void>(`/companies/${companyId}/follow`, {
        method: follow ? "POST" : "DELETE",
      }),
    onSuccess: (_data, { companyId, follow }) => {
      if (follow) capture("company_followed", { company_id: companyId });
      qc.invalidateQueries({ queryKey: companyKeys.followed() });
    },
    onError: (err) =>
      toastUtil(err.message || "No se pudo actualizar el seguimiento.", "error"),
  });
}
